import React, { FC, ReactElement, useContext } from 'react';
import classNames from 'classnames';

import styles from './styles.module.scss';

import { ADDITIONAL_ORGANIZATION_TABS, BASE_ORGANIZATION_TABS, MEMBERS } from 'constants/organizationTabs';

import { OrganizationContext } from 'context/organizationContext';
import { useAppSelector } from '../../../../../../store';
import { LinkUtils } from '../../../../../../utils';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';

interface TabsControllerProps {
    isInTop100: boolean;
}

export const TabsController: FC<TabsControllerProps> = ({ isInTop100 }): ReactElement => {
    const { organizationDataTabSelected, changeOrganizationDataTab } = useContext(OrganizationContext);
    const { premiumUserData } = useAppSelector(({ premiumSubscription }) => premiumSubscription);

    const { organizationTabs } = useAppSelector(({ organizationTabs }) => organizationTabs);
    const {
        organizationData: { id: organizationId, isDefaultPremium },
    } = useAppSelector(({ organization }) => organization);

    const isPremiumAllowed = isDefaultPremium || premiumUserData.isPremiumAllowed;

    const isMobile = useMediaQuery('(max-width:575.98px)');
    const isTablet = useMediaQuery('(max-width:767.98px)');

    if (isInTop100) {
        const proTabsName = organizationTabs.data.map((ot) => ot.tab.name.toLowerCase());
        const tabNameMap: Record<string, string> = {
            'exec. summary': 'executive summary',
            member: 'members',
        };

        const allPossibleTabs = ADDITIONAL_ORGANIZATION_TABS.map(tab => {
            const normalizedTabName = tabNameMap[tab.tabName] || tab.tabName;
            const isTabAvailableForOrg = proTabsName.includes(normalizedTabName.toLowerCase());
            const isTabPremium = tab.premium;
            
            return {
                ...tab,
                isBlocked: isTabPremium && !isPremiumAllowed && tab.tabName !== 'members',
                isAvailableForOrg: isTabAvailableForOrg || tab.tabName === 'members'
            };
        });

        const additionalBaseTabs = BASE_ORGANIZATION_TABS.filter(baseTab => 
            !ADDITIONAL_ORGANIZATION_TABS.some(addTab => addTab.tabName === baseTab.tabName)
        ).map(tab => ({
            ...tab,
            isBlocked: false,
            isAvailableForOrg: !proTabsName.includes(tab.tabName.toLowerCase())
        }));

        const tabs = [...allPossibleTabs, ...additionalBaseTabs]
            .filter(tab => tab.isAvailableForOrg)
            .sort((a, b) => a.order - b.order);

        const tabsCount = isMobile ? 2 : isTablet ? 4 : tabs.length === 5 ? 5 : 6;
        const tabWidth = `calc(100% / ${tabsCount})`;

        return (
            <div className={styles.tabsWrapper}>
                <div className={styles.tabsContainer}>
                    {tabs.map(({ tabName, title, premium, isBlocked }) => (
                        <div
                            onClick={() => changeOrganizationDataTab(tabName)}
                            className={classNames(styles.tabItem, {
                                [styles.activeTab]: tabName === organizationDataTabSelected,
                                [styles.blockedTab]: isBlocked,
                            })}
                            style={{ width: tabWidth }}
                            key={tabName}
                        >
                            <div className={styles.itemContent}>
                                <span className={classNames({ [styles.blurredText]: isBlocked })}>{title}</span>
                                {premium ? (
                                    <div className={classNames(styles.premium, { [styles.blurredBadge]: isBlocked })}>
                                        <span className={styles.text}>PRO</span>
                                    </div>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    } else {
        const proTabsName = !isPremiumAllowed ? [] : organizationTabs.data.map((ot) => ot.tab.name.toLowerCase());
        const tabNameMap: Record<string, string> = {
            'exec. summary': 'executive summary',
            member: 'members',
        };

        const prodTabs = !isPremiumAllowed
            ? []
            : ADDITIONAL_ORGANIZATION_TABS.filter(({ tabName }) => {
                  const normalizedTabName = tabNameMap[tabName] || tabName;
                  const included = proTabsName.includes(normalizedTabName.toLowerCase());
                  return included;
              });
        const membersTab = ADDITIONAL_ORGANIZATION_TABS.find(({ tabName }) => tabName === MEMBERS);
        if (membersTab && !prodTabs.some(({ tabName }) => tabName === MEMBERS)) {
            prodTabs.push(membersTab);
        }

        const baseTabs = BASE_ORGANIZATION_TABS.filter(({ tabName }) => !proTabsName.includes(tabName.toLowerCase()));
        const tabs = [...baseTabs, ...prodTabs].sort((a, b) => a.order - b.order);

        const tabsCount = isMobile ? 2 : isTablet ? 4 : tabs.length === 5 ? 5 : 6;
        const tabWidth = `calc(100% / ${tabsCount})`;

        return (
            <div className={styles.tabsWrapper}>
                <div className={styles.tabsContainer}>
                    {tabs.map(({ tabName, title, premium }) => (
                        <div
                            onClick={() => changeOrganizationDataTab(tabName)}
                            className={classNames(styles.tabItem, {
                                [styles.activeTab]: tabName === organizationDataTabSelected,
                            })}
                            style={{ width: tabWidth }}
                            key={tabName}
                        >
                            <div className={styles.itemContent}>
                                <Link to={LinkUtils.createDynamicLink(organizationId, 'organization-tab', tabName)}>
                                    <span>{title}</span>
                                </Link>
                                {premium ? (
                                    <div className={styles.premium}>
                                        <span className={styles.text}>PRO</span>
                                    </div>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
};
